/**
 * A STATUS ALREADY NAMES ITS STAGE.
 *
 * `Status` belongs to exactly one `Stage` and `SubStatus` to exactly one
 * `Status` — so a filter holding `status=42` is a filter on that status's
 * stage, whether or not the Stage box was ever ticked.
 *
 * Both pickers used to ignore that and gate purely on the Stage value, so any
 * filter set that arrived with a status but no stage — a Favorite View saved
 * from a stage route (the route pinned the stage, so it was never a chip), a
 * report drill ↗, a shared link — rendered an empty "Pick stage", a Status box
 * reading "Select stages first", and NO sign of the status that was actually
 * filtering the list. The chip in the drawer said "Status: Offer Letter
 * Issued" while both dropdowns claimed nothing was picked.
 *
 * These resolve the implied selection for DISPLAY and for option lists only —
 * nothing here is written back into the filter state, so the query the server
 * receives is byte-identical. The user simply sees what they are looking at.
 */

type AnyStage = {
  id: number | string;
  statuses?: Array<{
    id: number | string;
    subStatuses?: Array<{ id: number | string }>;
  }>;
};

const ids = (list: readonly string[] | undefined) =>
  new Set((list ?? []).map(String).filter(Boolean));

/** Stages owning any of `statusIds`, or owning the parents of `subStatusIds`. */
export function impliedStageIds(
  pipeline: readonly AnyStage[] | undefined,
  statusIds: readonly string[] | undefined,
  subStatusIds: readonly string[] | undefined,
): string[] {
  const wantStatus = ids(statusIds);
  const wantSub = ids(subStatusIds);
  if (wantStatus.size === 0 && wantSub.size === 0) return [];

  const out: string[] = [];
  for (const stage of pipeline ?? []) {
    const hit = (stage.statuses ?? []).some(
      (st) =>
        wantStatus.has(String(st.id)) ||
        (st.subStatuses ?? []).some((ss) => wantSub.has(String(ss.id))),
    );
    if (hit) out.push(String(stage.id));
  }
  return out;
}

/** Statuses owning any of `subStatusIds`. */
export function impliedStatusIds(
  pipeline: readonly AnyStage[] | undefined,
  subStatusIds: readonly string[] | undefined,
): string[] {
  const wantSub = ids(subStatusIds);
  if (wantSub.size === 0) return [];

  const out: string[] = [];
  for (const stage of pipeline ?? []) {
    for (const st of stage.statuses ?? []) {
      if ((st.subStatuses ?? []).some((ss) => wantSub.has(String(ss.id)))) {
        out.push(String(st.id));
      }
    }
  }
  return out;
}

/**
 * The stage / status lists the pickers should DISPLAY and build options from:
 * what was explicitly picked, falling back to what the deeper selection
 * implies. Explicit always wins — a user who ticked stages sees exactly those.
 */
export function resolvePickerSelection(
  pipeline: readonly AnyStage[] | undefined,
  explicit: {
    stageIds: readonly string[];
    statusIds: readonly string[];
    subStatusIds: readonly string[];
  },
): { stageIds: string[]; statusIds: string[] } {
  const stageIds =
    explicit.stageIds.length > 0
      ? [...explicit.stageIds]
      : impliedStageIds(pipeline, explicit.statusIds, explicit.subStatusIds);
  const statusIds =
    explicit.statusIds.length > 0
      ? [...explicit.statusIds]
      : impliedStatusIds(pipeline, explicit.subStatusIds);
  return { stageIds, statusIds };
}
